// src/components/AdminLayout.jsx
//
// Shell for /admin/*. Dark pitch + lime accent — intentionally visually
// distinct from the rest of the app (which is light + lime) so it's
// obvious when you're in the "internal tooling" zone. Active item uses
// the lime swatch from the design system (#b5f03d), inactive items are
// zinc-300 on the pitch (#0d1f14) background.
//
// Lime/pitch tokens are hard-coded as arbitrary Tailwind colors here
// because the marketing site CSS variables aren't imported into the
// React app — and pulling them in just for the admin shell is overkill.

import { Link, Outlet, useLocation } from 'react-router-dom';

const NAV_GROUPS = [
  {
    items: [
      { path: '/admin/dashboard', label: '🏠 Dashboard' },
    ],
  },
  {
    heading: 'People',
    items: [
      { path: '/admin/users',            label: '👥 Users' },
      { path: '/admin/platform-claims',  label: '🔑 Platform Claims' },
    ],
  },
  {
    heading: 'Activity',
    items: [
      { path: '/admin/notifications',  label: '🔔 Notifications' },
      { path: '/admin/communications', label: '💬 Communications' },
    ],
  },
  {
    heading: 'Ratings',
    items: [
      { path: '/admin/ratings',        label: '⭐ Moderation' },
      { path: '/admin/ratings/clubs',  label: '🏟️ Club directory' },
      { path: '/admin/ratings/stats',  label: '📊 Stats' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { path: '/admin/clubs',          label: '🏟️ Clubs' },
      { path: '/admin/registrations',  label: '📝 Registrations' },
    ],
  },
  {
    heading: 'Compliance',
    items: [
      { path: '/admin/consent-audit',  label: '📜 SMS Consent Log' },
    ],
  },
  {
    heading: 'Tools',
    items: [
      { path: '/admin/seed-team-demo', label: '🧪 Seed Team Demo' },
    ],
  },
];

export default function AdminLayout() {
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <nav className="w-56 shrink-0 bg-[#0d1f14] flex flex-col border-r-4 border-[#b5f03d]">
        {/* Brand — lime accent strip + "ADMIN" wordmark */}
        <div className="px-4 py-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-1 h-7 bg-[#b5f03d]" />
            <span
              className="text-lg font-bold tracking-tight text-[#b5f03d] uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }}
            >
              Scrim<span className="text-white">ly</span> Admin
            </span>
          </div>
          <p className="mt-1 ml-3 text-[10px] uppercase tracking-widest text-zinc-500">
            Internal · founder only
          </p>
        </div>

        {/* Nav groups */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi}>
              {group.heading && (
                <p className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#b5f03d]/70">
                  {group.heading}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map(({ path, label }) => {
                  const active = location.pathname === path;
                  return (
                    <li key={path}>
                      <Link
                        to={path}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          active
                            ? 'bg-[#b5f03d] text-[#0d1f14] font-semibold'
                            : 'text-zinc-300 hover:bg-zinc-800 hover:text-white'
                        }`}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer hint that you're in admin */}
        <div className="px-4 py-3 border-t border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-600">
          You are in admin mode
        </div>
      </nav>

      <main className="flex-1 p-6 overflow-auto bg-zinc-50">
        <Outlet />
      </main>
    </div>
  );
}
